import type { IconName, ListItemOption } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { SubChartOptionEnable } from "@coreapp/utils/SubChartOptionEnable";
import { kBoxFormatCommands, type BoxFormatCommand } from "@coreapp/utils/ExecuteBoxFormatCommand";
import { BuildBranchStyleOptions } from "@/appComponents/Navigation/ContextualToolbar/ToolbarOptions";
import { GetWSMgr } from "@/Utils/StateLib";
import type { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { kCBBoxLayoutContent, kCBBoxOverrideContent } from "@coreapp/constants";

export type EditBoxOption = {
  label: string;
  dataTestId: string;
  value: string;
  icon: IconName;
  clickHandler?: () => void;
  disabled?: boolean;
  divider?: boolean;
  options?: ListItemOption[];
  listProps?: Record<string, unknown>;
};

// Structure actions go in the icon row; each "other" group is a separated overflow section.
export type EditBoxOptionGroups = { structure: EditBoxOption[]; other: EditBoxOption[][] };

export type GetEditBoxOptionsParams = {
  // Enablement is evaluated for this box, which may differ from the selection (a selected cell).
  targetBox: QChartBox;
  onCommand: (cmd: string) => () => void;
  onBranchStyleChange: (target: { value: string }) => void;
  onFormatCommand: (cmd: BoxFormatCommand) => () => void;
  onAddToLibrary: (() => void) | null;
  hasFormatOverrides: boolean;
};

export const getSubChartOptionState = (targetBox: QChartBox) => {
  const wsMgr = GetWSMgr();
  if (!wsMgr) {
    return { fEnableInsertBreak: false, fEnableRemoveBreak: false };
  }

  return SubChartOptionEnable(wsMgr, null, targetBox);
};

function getBranchOptions({
  targetBox,
  onCommand,
  onBranchStyleChange
}: GetEditBoxOptionsParams): EditBoxOption[] {
  const subchartOptionState = getSubChartOptionState(targetBox);

  return [
    {
      label: appstr("branchstyles"),
      dataTestId: "ctx-menu-branch-styles",
      value: "branchstyles",
      icon: "network-wired-regular",
      options: BuildBranchStyleOptions(onBranchStyleChange) as ListItemOption[],
      listProps: { grid: true, gridCols: 4 }
    },
    {
      label: appstr("insertbreak"),
      dataTestId: "ctx-menu-insert-break",
      disabled: !subchartOptionState.fEnableInsertBreak,
      value: "insertbreak",
      icon: "arrow-down-from-dotted-line",
      clickHandler: onCommand("insertbreak")
    },
    {
      label: appstr("removebreak"),
      dataTestId: "ctx-menu-remove-break",
      disabled: !subchartOptionState.fEnableRemoveBreak,
      value: "removebreak",
      icon: "arrow-up-from-dotted-line",
      clickHandler: onCommand("removebreak")
    }
  ];
}

function getClearFormatOptions({
  onFormatCommand,
  hasFormatOverrides
}: GetEditBoxOptionsParams): EditBoxOption[] {
  if (!hasFormatOverrides) return [];

  return [
    {
      label: appstr("clearformat"),
      dataTestId: "ctx-menu-clearformat",
      value: "clearformat",
      icon: "broom-wide",
      clickHandler: onFormatCommand(kBoxFormatCommands.clearFormat)
    }
  ];
}

function getFormatReuseOptions({
  onFormatCommand,
  onAddToLibrary,
  hasFormatOverrides
}: GetEditBoxOptionsParams): EditBoxOption[] {
  if (!hasFormatOverrides) return [];

  const libraryOptions: EditBoxOption[] = onAddToLibrary
    ? [
        {
          label: appstr("Add to library"),
          dataTestId: "ctx-menu-add-to-library",
          value: "addtolibrary",
          icon: "books",
          clickHandler: onAddToLibrary
        }
      ]
    : [];

  return [
    ...libraryOptions,
    {
      label: appstr("Create rule"),
      dataTestId: "ctx-menu-create-rule",
      value: "createrule",
      icon: "eye",
      clickHandler: onFormatCommand(kBoxFormatCommands.createRule)
    }
  ];
}

function getFormatOptions(params: GetEditBoxOptionsParams): EditBoxOption[] {
  return [
    {
      label: appstr("copyformat"),
      dataTestId: "ctx-menu-copyformat",
      value: "copyformat",
      icon: "paintbrush",
      clickHandler: params.onCommand("copyformat")
    },
    ...getClearFormatOptions(params)
  ];
}

const kFormatClipboardTypes: string[] = [kCBBoxLayoutContent, kCBBoxOverrideContent];

function canPasteFormat(targetBox: QChartBox): boolean {
  const wsMgr = GetWSMgr();
  const contentType = wsMgr?.fClipboard?.GetContentType();
  return (
    !!wsMgr &&
    kFormatClipboardTypes.includes(contentType) &&
    !!wsMgr.CanCustomPaste(contentType, targetBox)
  );
}

function getFormatOnlyOptions(params: GetEditBoxOptionsParams): EditBoxOption[] {
  return [
    ...getFormatOptions(params),
    {
      label: appstr("Paste"),
      dataTestId: "ctx-menu-paste",
      value: "Paste",
      icon: "paste",
      disabled: !canPasteFormat(params.targetBox),
      clickHandler: params.onCommand("paste")
    }
  ];
}

function getClipboardOptions(params: GetEditBoxOptionsParams): EditBoxOption[] {
  const { onCommand } = params;

  return [
    {
      label: appstr("Copy"),
      dataTestId: "ctx-menu-copy",
      value: "Copy",
      icon: "copy",
      clickHandler: onCommand("copy")
    },
    {
      label: appstr("Cut"),
      dataTestId: "ctx-menu-cut",
      value: "Cut",
      icon: "scissors",
      clickHandler: onCommand("cut")
    },
    ...getFormatOptions(params),
    {
      label: appstr("Paste"),
      dataTestId: "ctx-menu-paste",
      value: "Paste",
      icon: "paste",
      clickHandler: onCommand("paste")
    }
  ];
}

export function getEditEmployeeBoxOptions(
  params: GetEditBoxOptionsParams & { isPositionDoc: boolean; isAgentBox: boolean }
): EditBoxOptionGroups {
  const { onCommand, isPositionDoc, isAgentBox } = params;
  const deleteOption: EditBoxOption = {
    label: appstr("Delete"),
    dataTestId: "ctx-menu-delete",
    value: "Delete",
    icon: "trash-can",
    clickHandler: onCommand("DeleteSelection")
  };

  if (isPositionDoc) {
    return {
      structure: [deleteOption],
      other: [getFormatOnlyOptions(params), getFormatReuseOptions(params)]
    };
  }

  const addOptions: EditBoxOption[] = isAgentBox
    ? []
    : [
        {
          label: appstr("addassist"),
          dataTestId: "ctx-menu-addassist",
          value: "addassist",
          icon: "diagram-subtask",
          clickHandler: onCommand("AddAssistant")
        },
        {
          label: appstr("addsub"),
          dataTestId: "ctx-menu-addsub",
          value: "addsub",
          icon: "user-group",
          clickHandler: onCommand("addsub")
        }
      ];

  return {
    structure: [...addOptions, deleteOption],
    other: [getBranchOptions(params), getClipboardOptions(params), getFormatReuseOptions(params)]
  };
}

export function getEditPositionBoxOptions(params: GetEditBoxOptionsParams): EditBoxOptionGroups {
  const { onCommand } = params;

  return {
    structure: [
      {
        label: appstr("Add subordinate position"),
        dataTestId: "ctx-menu-addsub",
        value: "addsub",
        icon: "diagram-subtask",
        clickHandler: onCommand("addsub")
      },
      {
        label: appstr("Add assistant position"),
        dataTestId: "ctx-menu-addassist",
        value: "addassist",
        icon: "user-plus",
        clickHandler: onCommand("AddAssistant")
      },
      {
        label: appstr("Add entity", { entity: appstr("Employee").toLowerCase() }),
        dataTestId: "ctx-menu-addemployee",
        value: "addholder",
        icon: "user-group",
        clickHandler: onCommand("AddHolder")
      },
      {
        label: appstr("Delete"),
        dataTestId: "ctx-close-position",
        value: "closeposition",
        icon: "trash-can",
        clickHandler: onCommand("DeleteSelection")
      }
    ],
    other: [getBranchOptions(params), getFormatOnlyOptions(params), getFormatReuseOptions(params)]
  };
}
